/**
 * usePanicKey — universal "stop everything" keyboard shortcut.
 *
 * Cmd/Ctrl + . (period) is the industry-standard "panic" shortcut in
 * Pro Tools, Logic, Ableton, Cubase, FL Studio. Pressing it stops every
 * active transport at once:
 *   - Recording (single-channel and multi-track tracking)
 *   - Playback
 *   - Any preview audio elements you pass in
 *   - Optional custom panic actions (Sora cancel, stem cancel, etc.)
 *
 * Hook is intentionally side-effect free — it only fires the callbacks you
 * provide. Skips firing when focus is in an input/textarea/select so users
 * typing a literal period in a name field don't kill takes.
 *
 * iter-158 — added in response to a recurring "I can't stop the recording"
 * UX issue, alongside the more discoverable inline pill in the transport.
 *
 * @param {object} actions
 * @param {boolean} actions.isRecording   — true when a take is active
 * @param {()=>void} actions.stopRecording — called if isRecording is true
 * @param {boolean} actions.playing       — true when playback is active
 * @param {()=>void} actions.stopPlayback  — called if playing is true
 * @param {()=>void} [actions.extra]      — optional extra panic action
 *                                          (cancel Sora, cancel stems, etc.)
 * @param {()=>void} [actions.onPanic]    — called after stops fire (toast etc.)
 */
import { useEffect } from "react";

export default function usePanicKey({ isRecording, stopRecording, playing, stopPlayback, extra, onPanic }) {
  useEffect(() => {
    const handler = (e) => {
      // Cmd+. on macOS or Ctrl+. on Windows/Linux. Period is ".":
      // we check both e.key and e.code so Logitech layouts and physical
      // QWERTY both fire it.
      const isPanic = (e.metaKey || e.ctrlKey) && (e.key === "." || e.code === "Period");
      if (!isPanic) return;
      // Don't fire when typing in a form field.
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return;
      if (e.target?.isContentEditable) return;
      e.preventDefault();
      e.stopPropagation();
      let stopped = 0;
      if (isRecording && typeof stopRecording === "function") {
        try { stopRecording(); stopped++; } catch (err) { void err; }
      }
      if (playing && typeof stopPlayback === "function") {
        try { stopPlayback(); stopped++; } catch (err) { void err; }
      }
      if (typeof extra === "function") {
        try { extra(); stopped++; } catch (err) { void err; }
      }
      if (typeof onPanic === "function") {
        try { onPanic(stopped); } catch (err) { void err; }
      }
    };
    window.addEventListener("keydown", handler, true); // capture phase wins over modals
    return () => window.removeEventListener("keydown", handler, true);
  }, [isRecording, stopRecording, playing, stopPlayback, extra, onPanic]);
}
